import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, ArrowRight, ShieldCheck, Wallet, ClipboardCheck, Building2, CheckCircle2, Search, Globe, Bug, ClipboardList, Package, Smartphone, Database, MessageSquare, MousePointerClick, PenTool, Star, ListChecks, Languages, BadgeCheck, UserCheck, BookOpenCheck, ShieldAlert, Globe2, LifeBuoy, UserRound } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Logo, ThemeToggle } from "../components/Brand";
import { useAuth, homeFor } from "../context/AuthContext";
import { useFetch } from "../lib/api";
import { useI18n, LANGS, withLang } from "../i18n";

export const ICONS = { Globe, Bug, ClipboardList, Package, Smartphone, Database, MessageSquare, MousePointerClick, PenTool, Star, ListChecks };
const LINKS = [["/how-it-works", "how"], ["/categories", "categories"], ["/businesses", "businesses"], ["/pricing", "solutions"], ["/faq", "faq"], ["/about", "about"]];
export const Arrow = () => <ArrowRight className="h-4 w-4 ms-1 rtl:rotate-180" />;

function LangSwitch() {
  const { lang, base } = useI18n();
  const nav = useNavigate();
  return (
    <Select value={lang} onValueChange={(v) => nav(withLang(v, base) + window.location.hash)}>
      <SelectTrigger className="h-9 w-auto gap-1.5 border-0 bg-transparent px-2 text-sm" aria-label="Language" data-testid="language-switcher"><Languages className="h-4 w-4" /><SelectValue /></SelectTrigger>
      <SelectContent>{LANGS.map(([c, n]) => <SelectItem key={c} value={c} data-testid={`language-option-${c}`}>{n}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function PublicLayout() {
  const { user } = useAuth();
  const { t, p } = useI18n();
  const [open, setOpen] = useState(false);
  const links = LINKS.map(([to, k]) => (
    <NavLink key={to} to={p(to)} onClick={() => setOpen(false)} data-testid={`public-nav-${to.slice(1)}`} className={({ isActive }) => `whitespace-nowrap text-sm font-medium transition-colors ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>{t.nav[k]}</NavLink>
  ));
  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-6 px-4 sm:px-6 lg:px-8">
          <Link to={p("/")} aria-label="RewardLance"><Logo priority /></Link>
          <nav className="hidden lg:flex items-center gap-6">{links}</nav>
          <div className="flex-1" />
          <LangSwitch />
          <ThemeToggle />
          {user ? <Button asChild data-testid="go-dashboard-button"><Link to={homeFor(user.role)}>{t.nav.dashboard}</Link></Button> : (
            <div className="hidden sm:flex gap-2">
              <Button variant="ghost" asChild data-testid="header-login-button"><Link to="/login">{t.nav.login}</Link></Button>
              <Button asChild className="rounded-full" data-testid="header-register-button"><Link to="/register">{t.nav.start}</Link></Button>
            </div>
          )}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><Button variant="ghost" size="icon" className="lg:hidden" aria-label={t.nav.menu} data-testid="public-mobile-menu"><Menu className="h-5 w-5" /></Button></SheetTrigger>
            <SheetContent side="right" className="flex flex-col gap-5 pt-12">{links}<Link to="/login" className="text-sm font-medium" onClick={() => setOpen(false)}>{t.nav.login}</Link><Link to="/register" className="text-sm font-medium text-primary" onClick={() => setOpen(false)}>{t.nav.register}</Link></SheetContent>
          </Sheet>
        </div>
      </header>
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}

function Footer() {
  const { t, p } = useI18n();
  const cls = "text-muted-foreground hover:text-foreground";
  return (
    <footer className="border-t mt-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 grid gap-8 sm:grid-cols-4 text-sm">
        <div className="sm:col-span-2 space-y-3"><Logo /><p className="text-muted-foreground max-w-sm">{t.footer.tagline}</p></div>
        <div className="space-y-2 flex flex-col">{LINKS.map(([to, k]) => <Link key={to} to={p(to)} className={cls}>{t.nav[k]}</Link>)}</div>
        <div className="space-y-2 flex flex-col"><Link to={p("/terms")} className={cls}>{t.nav.terms}</Link><Link to={p("/privacy")} className={cls}>{t.nav.privacy}</Link><Link to={p("/contact")} className={cls} data-testid="footer-contact-link">{t.nav.contact}</Link>
          <div className="pt-2 flex flex-wrap gap-x-3 gap-y-1" data-testid="footer-languages">{LANGS.map(([c, n]) => <Link key={c} to={withLang(c, "/")} hrefLang={c} className="text-xs text-muted-foreground hover:text-foreground">{n}</Link>)}</div>
        </div>
      </div>
      <p className="text-center text-xs text-muted-foreground pb-8 px-4">© 2026 RewardLance. {t.footer.rights} · Level 1, One Mayfair Place, Mayfair, London, W1J 8AJ, United Kingdom · <a href="mailto:info@rewardlance.com" className="hover:text-foreground">info@rewardlance.com</a></p>
    </footer>
  );
}

export const Wrap = ({ children, className = "" }) => <section className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>;
export const Eyebrow = ({ children }) => <p className="text-sm font-medium text-primary mb-3">{children}</p>;
export const H2 = ({ children, className = "" }) => <h2 className={`font-display text-2xl md:text-3xl font-bold tracking-tight max-w-2xl ${className}`}>{children}</h2>;
export const Prose = ({ title, sub, paras, className = "" }) => (
  <Wrap className={`py-14 ${className}`}><div className="max-w-3xl"><H2>{title}</H2>{sub && <p className="mt-2 font-semibold text-primary">{sub}</p>}<div className="mt-6 space-y-4 text-muted-foreground leading-relaxed">{paras.map((x) => <p key={x.slice(0, 40)}>{x}</p>)}</div></div></Wrap>
);
export const QA = ({ items, id }) => (
  <Accordion type="single" collapsible>{items.map(([q, a], i) => <AccordionItem key={q} value={`${id}-${i}`}><AccordionTrigger className="text-start" data-testid={`faq-${id}-${i}`}>{q}</AccordionTrigger><AccordionContent className="text-muted-foreground leading-relaxed">{a}</AccordionContent></AccordionItem>)}</Accordion>
);
const Check = ({ children, light }) => <li className="flex gap-2"><CheckCircle2 className={`h-5 w-5 shrink-0 ${light ? "text-blue-300" : "text-primary"}`} />{children}</li>;

function HeroVisual({ v }) {
  return (
    <div className="relative">
      <div className="absolute -inset-6 rounded-3xl bg-primary/10 blur-3xl" />
      <Card className="relative rounded-2xl p-5 shadow-xl">
        <div className="wallet-card rounded-xl p-6 text-white">
          <p className="text-xs uppercase tracking-widest text-blue-200">{v.balance}</p>
          <p className="font-mono text-4xl font-semibold mt-2" dir="ltr">$346.75</p>
          <div className="mt-6 grid grid-cols-3 gap-3 text-xs">{[[v.pending, "$48.50"], [v.lifetime, "$1,284.30"], [v.withdrawn, "$889.05"]].map(([l, a]) => <div key={l}><p className="text-blue-200">{l}</p><p className="font-mono text-base" dir="ltr">{a}</p></div>)}</div>
        </div>
        <div className="mt-4 space-y-2">
          {v.tasks.map(([tt, s], i) => (
            <div key={tt} className="flex items-center justify-between rounded-lg border px-3 py-2.5 text-sm"><span className="truncate">{tt}</span><span className="flex items-center gap-3"><span className="text-xs text-muted-foreground hidden sm:inline">{s}</span><span className="font-mono font-semibold text-primary" dir="ltr">{["$8.50", "$4.00", "$12.00"][i]}</span></span></div>
          ))}
        </div>
      </Card>
    </div>
  );
}

const DEMO_STATS = [["692", "tasks", "stat-tasks"], ["127", "businesses", "stat-businesses"], ["$78,672", "rewards", "stat-rewards"]];

function HomeHero({ h }) {
  const { p } = useI18n();
  return (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40 [mask-image:radial-gradient(ellipse_at_top_left,black,transparent_70%)]" />
      <Wrap className="relative grid gap-12 lg:grid-cols-2 items-center py-20 lg:py-28">
        <div className="stagger">
          <Eyebrow>{h.eyebrow}</Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1]">{h.h1}</h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl">{h.intro}</p>
          {[h.intro2, h.intro3].filter(Boolean).map((x) => <p key={x.slice(0, 30)} className="mt-3 text-sm md:text-base text-muted-foreground max-w-xl">{x}</p>)}
          {h.tagline && <p className="mt-4 text-sm font-semibold text-primary" data-testid="hero-tagline">{h.tagline}</p>}
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" asChild className="rounded-full" data-testid="hero-register-button"><Link to="/register">{h.ctaPrimary} <Arrow /></Link></Button>
            <Button size="lg" variant="outline" asChild className="rounded-full" data-testid="hero-business-button"><Link to={p("/businesses")}>{h.ctaSecondary}</Link></Button>
          </div>
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">{DEMO_STATS.map(([n, k, id]) => <div key={k}><p className="font-mono text-2xl font-semibold" dir="ltr" data-testid={id}>{n}</p><p className="text-xs text-muted-foreground">{h.stats[k]}</p></div>)}</div>
        </div>
        <HeroVisual v={h.visual} />
      </Wrap>
    </div>
  );
}

function Audience({ a }) {
  const { p } = useI18n();
  return (
    <Wrap className="py-16">
      <Eyebrow>{a.eyebrow}</Eyebrow><H2>{a.title}</H2>{a.sub && <p className="mt-3 text-muted-foreground max-w-2xl">{a.sub}</p>}
      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        {[[UserRound, a.contributors, "/register", false], [Building2, a.businesses, p("/businesses"), true]].map(([I, s, to, dark]) => (
          <Card key={s.title} className={`p-8 rounded-2xl ${dark ? "bg-[#0B1224] text-white border-0" : ""}`} data-testid={`home-audience-${dark ? "business" : "contributor"}`}>
            <I className={`h-6 w-6 ${dark ? "text-sky-400" : "text-primary"}`} />
            <h3 className="font-display text-xl font-bold mt-4">{s.title}</h3>
            <p className={`mt-3 leading-relaxed ${dark ? "text-white/70" : "text-muted-foreground"}`}>{s.body}</p>
            <ul className="mt-5 space-y-2 text-sm">{s.points.map((x) => <Check key={x} light={dark}>{x}</Check>)}</ul>
            <Button asChild variant={dark ? "secondary" : "default"} className="mt-6 rounded-full"><Link to={to}>{s.cta || (dark ? a.businesses.title : a.contributors.title)} <Arrow /></Link></Button>
          </Card>
        ))}
      </div>
    </Wrap>
  );
}

const WHY_ICONS = [BadgeCheck, UserCheck, BookOpenCheck, ShieldAlert, Globe2, LifeBuoy];
export const IconGrid = ({ items, icons, cols = "lg:grid-cols-3" }) => (
  <div className={`mt-10 grid gap-5 sm:grid-cols-2 ${cols}`}>{items.map(([tt, d], i) => { const I = icons[i % icons.length]; return <Card key={tt} className="p-6 rounded-2xl transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-md"><I className="h-6 w-6 text-primary" /><h3 className="mt-4 font-semibold">{tt}</h3><p className="mt-2 text-sm text-muted-foreground leading-relaxed">{d}</p></Card>; })}</div>
);

function HomeFaq({ f }) {
  const { p } = useI18n();
  return (
    <Wrap className="py-16 grid gap-10 lg:grid-cols-3">
      <div><Eyebrow>{f.eyebrow}</Eyebrow><H2>{f.title}</H2><Link to={p("/faq")} className="mt-6 inline-flex items-center text-sm font-medium text-primary" data-testid="home-faq-more">{f.more} <Arrow /></Link></div>
      <div className="lg:col-span-2"><QA items={f.items} id="home" /></div>
    </Wrap>
  );
}

function HomeCta({ c }) {
  const { p } = useI18n();
  return (
    <Wrap className="py-16">
      <div className="rounded-2xl border bg-muted/40 p-10 md:p-14 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
        <div className="max-w-2xl"><H2>{c.title}</H2>{c.sub && <p className="mt-2 font-semibold">{c.sub}</p>}<p className="mt-3 text-muted-foreground">{c.body}</p>
          {c.cards && <div className="mt-6 grid gap-4 sm:grid-cols-2">{c.cards.map(([tt, d]) => <div key={tt}><p className="font-semibold">{tt}</p><p className="text-sm text-muted-foreground mt-1">{d}</p></div>)}</div>}</div>
        <div className="flex flex-wrap gap-3"><Button asChild size="lg" className="rounded-full" data-testid="home-cta-register"><Link to="/register">{c.primary}</Link></Button><Button asChild size="lg" variant="outline" className="rounded-full" data-testid="home-cta-proposal"><Link to={p("/pricing") + "#enquiry"}>{c.secondary}</Link></Button></div>
      </div>
    </Wrap>
  );
}

export function Home() {
  const { t, p } = useI18n();
  const h = t.home;
  const { data: cats } = useFetch("/public/categories");
  return (
    <>
      <HomeHero h={h} />
      <Wrap className="py-16">
        <Eyebrow>{h.steps.eyebrow}</Eyebrow><H2>{h.steps.title}</H2>{h.steps.sub && <p className="mt-3 text-muted-foreground max-w-2xl">{h.steps.sub}</p>}
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {h.steps.items.map(([tt, d], i) => { const I = [ClipboardCheck, ShieldCheck, Wallet][i]; return <Card key={tt} className="p-6 rounded-xl hover:-translate-y-1 transition-transform duration-300"><span className="font-mono text-xs text-muted-foreground">0{i + 1}</span><I className="h-6 w-6 text-primary mt-4" /><h3 className="font-semibold mt-4">{tt}</h3><p className="text-sm text-muted-foreground mt-2">{d}</p></Card>; })}
        </div>
        {h.steps.tagline && <p className="mt-6 text-sm font-semibold text-primary">{h.steps.tagline}</p>}
      </Wrap>
      <Audience a={h.audience} />
      <Wrap className="py-16">
        <div className="flex items-end justify-between mb-8"><div><Eyebrow>{h.cats.eyebrow}</Eyebrow><H2>{h.cats.title}</H2>{h.cats.sub && <p className="mt-3 text-muted-foreground max-w-2xl">{h.cats.sub}</p>}</div><Link to={p("/categories")} className="text-sm font-medium text-primary inline-flex items-center shrink-0" data-testid="home-all-categories">{h.cats.all} <Arrow /></Link></div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{(cats || []).slice(0, 8).map((c) => <CategoryCard key={c.id} c={c} />)}</div>
      </Wrap>
      <Wrap className="py-16"><Eyebrow>{h.why.eyebrow}</Eyebrow><H2>{h.why.title}</H2>{h.why.sub && <p className="mt-3 text-muted-foreground max-w-2xl">{h.why.sub}</p>}<IconGrid items={h.why.items} icons={WHY_ICONS} /></Wrap>
      <Wrap className="py-16">
        <div className="rounded-2xl wallet-card text-white p-10 md:p-14 grid gap-8 md:grid-cols-2 items-center">
          <div>{h.trust.eyebrow && <p className="text-sm font-medium text-blue-200 mb-2">{h.trust.eyebrow}</p>}<h2 className="font-display text-2xl md:text-3xl font-bold">{h.trust.title}</h2><p className="text-blue-100/80 mt-3 text-sm md:text-base">{h.trust.body}</p>{h.trust.body2 && <p className="text-blue-100/80 mt-3 text-sm md:text-base">{h.trust.body2}</p>}</div>
          <ul className="space-y-3 text-sm">{h.trust.points.map((x) => <Check key={x} light>{x}</Check>)}</ul>
        </div>
      </Wrap>
      <Prose title={h.seo.title} sub={h.seo.sub} paras={h.seo.paras} />
      <HomeFaq f={h.faq} />
      <HomeCta c={h.cta} />
    </>
  );
}

function CategoryCard({ c }) {
  const { t } = useI18n();
  const I = ICONS[c.icon] || ListChecks;
  const [name, desc] = t.categories.names[c.name] || [c.name, c.description];
  return (
    <Card className="p-5 rounded-xl hover:shadow-md hover:border-primary/40 transition-all duration-300" data-testid={`category-card-${c.id}`}>
      <I className="h-5 w-5 text-primary" />
      <h3 className="font-semibold mt-3 text-sm">{name}</h3>
      <p className="text-xs text-muted-foreground mt-1">{desc}</p>
      <p className="text-xs mt-3 font-mono">{c.paid_allowed ? `${c.task_count} ${t.home.cats.live}` : t.home.cats.voluntary}</p>
    </Card>
  );
}

export const Page = ({ eyebrow, title, intro, children }) => (
  <Wrap className="py-16 lg:py-20">
    <Eyebrow>{eyebrow}</Eyebrow>
    <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight max-w-3xl leading-[1.15]">{title}</h1>
    {intro && <p className="mt-5 text-muted-foreground max-w-2xl">{intro}</p>}
    <div className="mt-12">{children}</div>
  </Wrap>
);

export function Categories() {
  const { t } = useI18n();
  const c = t.categories;
  const { data } = useFetch("/public/categories");
  const [q, setQ] = useState("");
  const list = (data || []).filter((x) => (c.names[x.name]?.[0] || x.name).toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <Page eyebrow={c.eyebrow} title={c.title} intro={c.intro}>
        <div className="relative max-w-sm mb-8"><Search className="absolute start-3 top-2.5 h-4 w-4 text-muted-foreground" /><Input className="ps-9" placeholder={c.search} value={q} onChange={(e) => setQ(e.target.value)} data-testid="category-search-input" /></div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{list.map((x) => <CategoryCard key={x.id} c={x} />)}</div>
      </Page>
      <Wrap className="py-10"><H2>{c.guide.title}</H2><IconGrid items={c.guide.items} icons={[ClipboardList, UserCheck, ShieldCheck]} /></Wrap>
      <Prose title={c.detail.title} paras={c.detail.paras} />
    </>
  );
}

export function ForBusinesses() {
  const { t, p } = useI18n();
  const b = t.businesses;
  return (
    <>
      <Page eyebrow={b.eyebrow} title={b.title} intro={b.intro}>
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="space-y-4">{b.features.map(([tt, d], i) => { const I = [Building2, ShieldCheck, ClipboardCheck, Globe2, BookOpenCheck, Wallet][i]; return <Card key={tt} className="p-5 rounded-xl flex gap-4"><I className="h-5 w-5 text-primary shrink-0 mt-0.5" /><div><h2 className="font-semibold">{tt}</h2><p className="text-sm text-muted-foreground">{d}</p></div></Card>; })}
            <Button asChild variant="outline" className="rounded-full" data-testid="business-register-button"><Link to="/register?type=business">{b.register}</Link></Button></div>
          <Card className="p-8 rounded-2xl bg-[#0B1224] text-white border-0 flex flex-col justify-between gap-6 self-start lg:sticky lg:top-24" data-testid="business-proposal-card">
            <div>
              <p className="text-sm text-white/60">{b.card.kicker}</p>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mt-2">{b.card.title}</h2>
              <p className="mt-4 text-white/70">{b.card.body}</p>
              <ul className="mt-6 space-y-2 text-sm">{b.card.points.map((x) => <Check key={x} light>{x}</Check>)}</ul>
            </div>
            <Button asChild size="lg" className="rounded-full self-start" data-testid="business-proposal-cta"><Link to={p("/pricing") + "#enquiry"}>{b.card.cta} <Arrow /></Link></Button>
          </Card>
        </div>
      </Page>
      <Wrap className="py-10"><H2>{b.useCases.title}</H2><IconGrid items={b.useCases.items} icons={[MousePointerClick, Bug, MessageSquare, ClipboardList, Star, Database]} /></Wrap>
      <Wrap className="py-14"><H2>{b.industries.title}</H2><div className="mt-8 flex flex-wrap gap-3">{b.industries.items.map((x) => <span key={x} className="rounded-full border px-4 py-2 text-sm">{x}</span>)}</div></Wrap>
      <Prose title={b.process.title} paras={b.process.paras} />
      <Prose title={b.seo.title} paras={b.seo.paras} className="pt-0" />
    </>
  );
}
